import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges, HostListener } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TrainingService } from '../../../../services/training/training.service';
import { TrainingContent } from '../../../../services/training/training.interface';

@Component({
  selector: 'app-training-content',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './training-content.component.html',
  styleUrl: './training-content.component.css',
})
export class TrainingContentComponent implements OnChanges, OnDestroy {
  @Input() contentItems: TrainingContent[] = [];
  @Input() moduleId = '';
  @Input() alreadyCompleted = false;
  @Input() isLastSection = false;
  @Input() sectionType: 'slideshow' | 'image' | 'video' = 'slideshow';
  @Input() isActive = true;
  @Output() sectionDone = new EventEmitter<void>();

  currentIndex = 0;
  currentUrl: string | null = null;
  loadingUrl = false;
  showDoneOverlay = false;
  private sectionDoneEmitted = false;
  private overlayTimer: ReturnType<typeof setTimeout> | null = null;

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isActive || this.sectionType !== 'slideshow') return;
    const tag = (event.target as HTMLElement).tagName.toLowerCase();
    if (['input', 'textarea', 'select'].includes(tag)) return;
    if (event.key === 'ArrowLeft') { this.prev(); event.preventDefault(); }
    else if (event.key === 'ArrowRight') { this.next(); event.preventDefault(); }
  }

  constructor(private trainingService: TrainingService, private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['contentItems'] || changes['moduleId']) && this.contentItems.length > 0) {
      this.currentIndex = 0;
      this.sectionDoneEmitted = false;
      this.fetchCurrentUrl();
      // Image: auto-complete as soon as it loads
      if (this.sectionType === 'image') {
        setTimeout(() => this.emitSectionDone());
      }
      // Slideshow with only one slide: auto-complete immediately
      if (this.sectionType === 'slideshow' && this.contentItems.length === 1) {
        setTimeout(() => this.emitSectionDone());
      }
    }
  }

  ngOnDestroy(): void {
    if (this.overlayTimer) clearTimeout(this.overlayTimer);
  }

  private emitSectionDone(): void {
    if (this.sectionDoneEmitted || this.alreadyCompleted) return;
    this.sectionDoneEmitted = true;
    this.showDoneOverlay = true;
    this.overlayTimer = setTimeout(() => { this.showDoneOverlay = false; }, 1500);
    this.sectionDone.emit();
  }

  get currentItem(): TrainingContent | null {
    return this.contentItems[this.currentIndex] ?? null;
  }

  get isFirst(): boolean {
    return this.currentIndex === 0;
  }

  get isLast(): boolean {
    return this.currentIndex === this.contentItems.length - 1;
  }

  get safeUrl(): SafeResourceUrl | null {
    if (!this.currentUrl) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.currentUrl);
  }

  prev(): void {
    if (!this.isFirst) {
      this.currentIndex--;
      this.fetchCurrentUrl();
    }
  }

  next(): void {
    if (!this.isLast) {
      this.currentIndex++;
      this.fetchCurrentUrl();
      if (this.isLast && this.sectionType === 'slideshow') {
        this.emitSectionDone();
      }
    }
  }

  onVideoEnded(): void {
    this.emitSectionDone();
  }

  fetchCurrentUrl(): void {
    const item = this.currentItem;
    if (!item) return;
    this.currentUrl = null;
    this.loadingUrl = true;
    this.trainingService.getContentUrl(this.moduleId, item._id).subscribe({
      next: ({ url }) => {
        this.currentUrl = url;
        this.loadingUrl = false;
      },
      error: () => { this.loadingUrl = false; },
    });
  }

  onVideoError(): void {
    this.fetchCurrentUrl();
  }
}
